import React, { useState, useCallback } from 'react';
import TimePicker from './TimePicker';
// Styles provided by RestaurantOnboarding.css

/* ─── Constants ─────────────────────────────────── */
const DAYS = [
  { key: 'L', name: 'Lunes',      short: 'Lun' },
  { key: 'M', name: 'Martes',     short: 'Mar' },
  { key: 'X', name: 'Miércoles',  short: 'Mié' },
  { key: 'J', name: 'Jueves',     short: 'Jue' },
  { key: 'V', name: 'Viernes',    short: 'Vie' },
  { key: 'S', name: 'Sábado',     short: 'Sáb' },
  { key: 'D', name: 'Domingo',    short: 'Dom' },
];
const DAY_SHORT = Object.fromEntries(DAYS.map((d) => [d.key, d.short]));
const DEFAULT_SHIFT = { start: '08:00', end: '22:00' };

const QUICK = [
  { label: 'Todos',       keys: ['L','M','X','J','V','S','D'] },
  { label: 'Lun – Vie',   keys: ['L','M','X','J','V'] },
  { label: 'Fin de sem.', keys: ['S','D'] },
];

/* ─── Helpers ────────────────────────────────────── */
const toMin = (s) => { if (!s) return 0; const [h, m] = s.split(':').map(Number); return h * 60 + (m || 0); };

/* Cap a time to [min, max] city range */
const capToRange = (time, min, max) => {
  if (!time) return min || DEFAULT_SHIFT.start;
  if (min && toMin(time) < toMin(min)) return min;
  if (max && toMin(time) > toMin(max)) return max;
  return time;
};

/* Returns the later of two HH:MM strings (or whichever is non-null) */
const laterTime = (a, b) => {
  if (!a) return b;
  if (!b) return a;
  return toMin(a) >= toMin(b) ? a : b;
};

/* Validate a day's shifts — returns an error string or null */
const shiftIssue = (shifts, cityRange = {}) => {
  const filled = shifts.filter((s) => s.start && s.end);
  for (const s of filled) {
    if (toMin(s.end) <= toMin(s.start)) return 'El cierre debe ser posterior a la apertura';
    if (cityRange.maxTime && toMin(s.end) > toMin(cityRange.maxTime))
      return `El horario supera la cobertura de la ciudad (máx. ${cityRange.maxTime})`;
    if (cityRange.minTime && toMin(s.start) < toMin(cityRange.minTime))
      return `El horario inicia antes de la cobertura (mín. ${cityRange.minTime})`;
  }
  const sorted = [...filled].sort((a, b) => toMin(a.start) - toMin(b.start));
  for (let i = 1; i < sorted.length; i++) {
    if (toMin(sorted[i].start) < toMin(sorted[i - 1].end))
        return 'Uno o más turnos se superponen en el mismo horario.';
  }
  return null;
};

const mkDay = (active = false) => ({
  active, open: false, shifts: [{ ...DEFAULT_SHIFT }],
});

const initDays = () =>
  Object.fromEntries(DAYS.map((d) => [d.key, mkDay()]));

const parseSchedule = (str) => {
  const days = initDays();
  if (!str) return days;
  str.split(';').filter(Boolean).forEach((part) => {
    const [key, start, end] = part.split(',');
    if (!days[key]) return;
    if (!days[key].active) {
      days[key] = { active: true, open: false, shifts: [{ start, end }] };
    } else {
      days[key].shifts.push({ start, end });
    }
  });
  return days;
};

const serialize = (days) =>
  DAYS.flatMap((d) =>
    days[d.key].active
      ? days[d.key].shifts.filter((s) => s.start && s.end).map((s) => `${d.key},${s.start},${s.end}`)
      : []
  ).join(';');

const formatCityHours = (hours) => {
  if (!hours?.length) return null;
  const allSame = hours.every((h) => h.start === hours[0].start && h.end === hours[0].end);
  if (allSame) return `${DAY_SHORT[hours[0].day]} – ${DAY_SHORT[hours[hours.length - 1].day]} · ${hours[0].start} – ${hours[0].end}`;
  return hours.map((h) => `${DAY_SHORT[h.day]} ${h.start}–${h.end}`).join(' · ');
};

const getCityRange = (dayKey, cityHours) => {
  if (!cityHours?.length) return {};
  const row = cityHours.find((h) => h.day === dayKey);
  if (!row) return {};
  return { minTime: row.start, maxTime: row.end };
};

/* ─── Toggle ─────────────────────────────────────── */
const Toggle = ({ on, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    className={`ro-toggle${on ? ' ro-toggle--on' : ''}`}
    onClick={(e) => { e.stopPropagation(); onChange(!on); }}
  />
);

/* ─── Day row ────────────────────────────────────── */
const DayRow = ({
  day, state, onToggle, onOpen, onShiftChange, onShiftAdd, onShiftDelete,
  onCopyAll, justCopied, cityRange = {}, flashId = 0,
}) => {
  const { active, open, shifts } = state;
  const isMulti = shifts.length > 1;
  const issue   = active ? shiftIssue(shifts, cityRange) : null;

  /* Compute effective min/max for each shift field */
  const shiftMin = (i, field) => {
    if (field === 'start') {
      const prevEnd = i > 0 ? shifts[i - 1]?.end : null;
      return laterTime(cityRange.minTime, prevEnd) || cityRange.minTime;
    }
    /* 'end' */
    return laterTime(cityRange.minTime, shifts[i]?.start) || cityRange.minTime;
  };

  return (
    <div className={`ro-sch-row${active ? ' ro-sch-row--on' : ''}`}>

      {/* Flash overlay — remounts on each copy via key, restarting the animation */}
      {flashId > 0 && (
        <div key={flashId} className="ro-sch-row-flash-overlay" aria-hidden="true" />
      )}

      {/* Main line */}
      <div className="ro-sch-row-main">
        <Toggle on={active} onChange={onToggle} />

        <span className={`ro-sch-day-lbl${!active ? ' ro-sch-day-lbl--off' : ''}`}>
          {day.name}
        </span>

        {active && !isMulti && (
          <div className="ro-sch-inline">
            <TimePicker
              value={shifts[0].start}
              onChange={(t) => onShiftChange(0, { ...shifts[0], start: t })}
              minTime={cityRange.minTime}
              maxTime={cityRange.maxTime}
            />
            <span className="ro-sch-dash">—</span>
            <TimePicker
              value={shifts[0].end}
              onChange={(t) => onShiftChange(0, { ...shifts[0], end: t })}
              minTime={shiftMin(0, 'end')}
              maxTime={cityRange.maxTime}
            />
          </div>
        )}

        {active && isMulti && (
          <span className="ro-sch-badge">{shifts.length} turnos</span>
        )}

        {!active && (
          <span className="ro-sch-closed">Cerrado</span>
        )}

        {/* Actions */}
        {active && (
          <div className="ro-sch-actions">
            {justCopied ? (
              <span className="ro-sch-copied">✓ Aplicado</span>
            ) : (
              <button
                type="button"
                className="ro-sch-act-btn ro-sch-act-btn--copy"
                onClick={onCopyAll}
                title="Aplicar este horario a todos los días"
              >
                ↻ Todos
              </button>
            )}
            <button
              type="button"
              className="ro-sch-act-btn"
              onClick={onShiftAdd}
              title="Agregar turno"
            >
              + Turno
            </button>
            {isMulti && (
              <button
                type="button"
                className="ro-sch-expand-btn"
                onClick={() => onOpen(!open)}
              >
                {open ? '▲' : '▾'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Expanded multi-shift panel */}
      {active && isMulti && open && (
        <div className="ro-sch-expanded">
          {shifts.map((shift, i) => (
            <div key={i} className="ro-sch-shift">
              <TimePicker
                value={shift.start}
                onChange={(t) => onShiftChange(i, { ...shift, start: t })}
                minTime={shiftMin(i, 'start')}
                maxTime={cityRange.maxTime}
              />
              <span className="ro-sch-dash">—</span>
              <TimePicker
                value={shift.end}
                onChange={(t) => onShiftChange(i, { ...shift, end: t })}
                minTime={shiftMin(i, 'end')}
                maxTime={cityRange.maxTime}
              />
                <button
                    type="button"
                    className="ro-sch-del"
                    onClick={() => onShiftDelete(i)}
                    aria-label="Eliminar turno"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M3 6h18" />
                        <path d="M8 6V4h8v2" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                        <path d="M5 6l1 14h12l1-14" />
                    </svg>
                </button>
            </div>
          ))}
        </div>
      )}

      {/* Shift issue banner */}
      {issue && (
        <div className="ro-sch-issue">
          <span className="ro-sch-issue-ico">⚠</span>
          {issue}
        </div>
      )}
    </div>
  );
};

/* ─── Main step ──────────────────────────────────── */
const StepRestaurantSchedule = ({ formData, onUpdate, errors = {} }) => {
  const cityHours    = formData.city_operating_hours;
  const cityMinHours = formData.city_min_hours ?? 0;
  const cityLabel    = formatCityHours(cityHours);
  const [cityOpen,  setCityOpen]  = useState(false);
  const [days,      setDays]      = useState(() => parseSchedule(formData.schedule));
  const [copiedKey, setCopiedKey] = useState(null);
  const [flashMap,  setFlashMap]  = useState({}); // dayKey → incrementing id

  const commit = useCallback((newDays) => {
    setDays(newDays);
    onUpdate({ schedule: serialize(newDays) });
  }, [onUpdate]);

  const updateDay = (key, patch) =>
    commit({ ...days, [key]: { ...days[key], ...patch } });

  /* Quick group selectors */
  const activateGroup = (keys) => {
    const newDays = { ...days };
    DAYS.forEach((d) => {
      if (keys.includes(d.key)) {
        newDays[d.key] = { ...newDays[d.key], active: true, open: false,
          shifts: newDays[d.key].shifts.length ? newDays[d.key].shifts : [{ ...DEFAULT_SHIFT }] };
      } else {
        newDays[d.key] = { ...newDays[d.key], active: false };
      }
    });
    commit(newDays);
  };

  const clearAll = () => commit(initDays());

  /* Copy a day's shifts to all days + flash animation */
  const copyToAll = (key) => {
    const src = days[key];
    const newDays = {};
    DAYS.forEach((d) => {
      newDays[d.key] = { active: true, open: false, shifts: src.shifts.map((s) => ({ ...s })) };
    });
    commit(newDays);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1800);

    /* Increment flash id for every row except the source */
    setFlashMap((prev) => {
      const next = { ...prev };
      DAYS.forEach((d) => {
        if (d.key !== key) next[d.key] = (prev[d.key] || 0) + 1;
      });
      return next;
    });
  };

  const handleToggle = (key, on) => {
    if (!on) {
      updateDay(key, { active: false, open: false });
      return;
    }
    // Cap shifts to city range when activating a day
    const range   = getCityRange(key, cityHours);
    const base    = days[key].shifts.length ? days[key].shifts : [{ ...DEFAULT_SHIFT }];
    const capped  = base.map((s) => ({
      start: capToRange(s.start, range.minTime, range.maxTime),
      end:   capToRange(s.end,   range.minTime, range.maxTime),
    }));
    updateDay(key, { active: true, open: false, shifts: capped });
  };

  const handleShiftChange = (key, i, updated) =>
    updateDay(key, { shifts: days[key].shifts.map((s, idx) => idx === i ? updated : s) });

  const handleShiftAdd = (key) =>
    updateDay(key, { shifts: [...days[key].shifts, { start: null, end: null }], open: true });

  const handleShiftDelete = (key, i) =>
    updateDay(key, { shifts: days[key].shifts.filter((_, idx) => idx !== i) });

  const activeCount = DAYS.filter((d) => days[d.key].active).length;

  /* ── Total weekly hours ── */
  const totalWeeklyMinutes = DAYS.reduce((sum, d) => {
    if (!days[d.key].active) return sum;
    return sum + days[d.key].shifts
      .filter((s) => s.start && s.end && toMin(s.end) > toMin(s.start))
      .reduce((s2, sh) => s2 + (toMin(sh.end) - toMin(sh.start)), 0);
  }, 0);
  const totalWeeklyHours = +(totalWeeklyMinutes / 60).toFixed(1);
  const minRequired      = cityMinHours || 0;
  const meetsMinimum     = minRequired <= 0 || totalWeeklyMinutes >= minRequired * 60;

  return (
    <div className="ro-body ro-basic">

      <div className="ro-step-hd ro-step-hd--sm">
        <span className="ro-eyebrow ro-eyebrow--accent">Paso 10 · Horarios</span>
        <h2 className="ro-title">Horario de atención</h2>
      </div>

      {/* ── Min hours notice ── */}
      {minRequired > 0 && (
        <div className="ro-sch-notice">
          <span className="ro-sch-notice-ico">ℹ</span>
          Tu negocio debe operar al menos <strong>{minRequired} hora{minRequired !== 1 ? 's' : ''} a la semana</strong> en esta ciudad.
        </div>
      )}

      {/* ── City coverage card ── */}
      {cityLabel && (
        <button type="button" className="ro-sch-city-card" onClick={() => setCityOpen((o) => !o)}>
          <span className="ro-sch-city-ico">🛵</span>
          <div className="ro-sch-city-text">
            <span className="ro-sch-city-title">Cobertura de repartidores</span>
            <span className="ro-sch-city-sub">{cityLabel}</span>
          </div>
          <span className={`ro-sch-panel-arr${cityOpen ? ' ro-sch-panel-arr--up' : ''}`}>▾</span>
        </button>
      )}

      {cityOpen && cityHours?.length > 0 && (
        <div className="ro-sch-city-grid">
          {cityHours.map((h, i) => (
            <div key={i} className="ro-sch-city-chip">
              <span className="ro-sch-city-chip-day">{DAY_SHORT[h.day]}</span>
              <span className="ro-sch-city-chip-time">{h.start}–{h.end}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Weekly hours counter ── */}
      <div className={`ro-sch-weekly${!meetsMinimum ? ' ro-sch-weekly--warn' : totalWeeklyMinutes > 0 ? ' ro-sch-weekly--ok' : ''}`}>
        <span className="ro-sch-weekly-lbl">Horas configuradas esta semana</span>
        <span className="ro-sch-weekly-val">
          {totalWeeklyHours}h
          {minRequired > 0 && (
            <span className="ro-sch-weekly-min"> / mín. {minRequired}h</span>
          )}
        </span>
        {minRequired > 0 && (
          <div className="ro-sch-weekly-bar">
            <div
              className="ro-sch-weekly-fill"
              style={{ width: `${Math.min(100, (totalWeeklyMinutes / (minRequired * 60)) * 100)}%` }}
            />
          </div>
        )}
      </div>

      {/* ── Quick selectors ── */}
      <div className="ro-sch-qk-bar">
        {QUICK.map((q) => (
          <button
            key={q.label}
            type="button"
            className="ro-sch-qk-btn"
            onClick={() => activateGroup(q.keys)}
          >
            {q.label}
          </button>
        ))}
        <button type="button" className="ro-sch-qk-btn ro-sch-qk-btn--clear" onClick={clearAll}>
          Limpiar
        </button>
        <span className="ro-sch-active-count">
          {activeCount} {activeCount === 1 ? 'día' : 'días'} activos
        </span>
      </div>

      {/* ── Day rows ── */}
      <div className="ro-sch-list">
        {DAYS.map((d) => (
          <DayRow
            key={d.key}
            day={d}
            state={days[d.key]}
            onToggle={(on)       => handleToggle(d.key, on)}
            onOpen={(open)       => updateDay(d.key, { open })}
            onShiftChange={(i,u) => handleShiftChange(d.key, i, u)}
            onShiftAdd={()       => handleShiftAdd(d.key)}
            onShiftDelete={(i)   => handleShiftDelete(d.key, i)}
            onCopyAll={()        => copyToAll(d.key)}
            justCopied={copiedKey === d.key}
            cityRange={getCityRange(d.key, cityHours)}
            flashId={flashMap[d.key] || 0}
          />
        ))}
      </div>

      {errors.schedule && (
        <span className="ro-err-msg ro-err-msg--block">{errors.schedule}</span>
      )}

    </div>
  );
};

export default StepRestaurantSchedule;
